import { match } from 'ts-pattern'

import type { SemanticType } from '../../../semanticTypes'
import { scopedSemanticTypeRuntimePath } from './utils'

/**
 * The parts of a quote's pricing that can be compared on their own. The
 * `compare_quote_pricing` executor also reports a phases outcome; `ALL` covers
 * it, so no comparison surfaces it separately yet.
 */
export const QUOTE_PRICING_COMPARISONS = [
  'ALL',
  'PRICES',
  'DISCOUNTS',
  'MINIMUMS'
] as const

export type QuotePricingComparison = (typeof QUOTE_PRICING_COMPARISONS)[number]

export const isQuotePricingComparison = (
  comparison: unknown
): comparison is QuotePricingComparison =>
  QUOTE_PRICING_COMPARISONS.includes(comparison as QuotePricingComparison)

/**
 * How each comparison reads. `label` names it in the dropdown; `subject` is the
 * noun the canvas clause is built around, so its number picks the comparison
 * labels.
 *
 *   ALL    -> "<subject> contract terms are equal"
 *   PRICES -> "<subject> prices are equal"
 */
export const QUOTE_PRICING_COMPARISON_DISPLAY: Record<
  QuotePricingComparison,
  { label: string; subject: string; isPlural: boolean }
> = {
  ALL: {
    label: 'All contract terms',
    subject: 'contract terms',
    isPlural: true
  },
  PRICES: { label: 'All prices', subject: 'prices', isPlural: true },
  DISCOUNTS: { label: 'All discounts', subject: 'discounts', isPlural: true },
  MINIMUMS: { label: 'All minimums', subject: 'minimums', isPlural: true }
}

export const QUOTE_COMPARISON_SUB_OPERATORS = [
  'IS_EQUAL',
  'IS_NOT_EQUAL'
] as const

export type QuoteComparisonSubOperator =
  (typeof QUOTE_COMPARISON_SUB_OPERATORS)[number]

export const isQuoteComparisonSubOperator = (
  operator: unknown
): operator is QuoteComparisonSubOperator =>
  QUOTE_COMPARISON_SUB_OPERATORS.includes(
    operator as QuoteComparisonSubOperator
  )

/**
 * Display labels for the equality comparison. Each compared field supplies its
 * own so the row agrees with its subject's number — a plural subject reads
 * "attachments are equal", a singular one "raw text is equal".
 */
export type QuoteComparisonLabels = {
  isEqual: string
  isNotEqual: string
}

export const DEFAULT_QUOTE_COMPARISON_LABELS: QuoteComparisonLabels = {
  isEqual: 'Is equal',
  isNotEqual: 'Is not equal'
}

/** A plural subject reads "attachments are equal", "prices are equal". */
export const PLURAL_QUOTE_COMPARISON_LABELS: QuoteComparisonLabels = {
  isEqual: 'Are equal',
  isNotEqual: 'Are not equal'
}

/**
 * What every quote-comparison row stores, whatever the quote is compared
 * against. A subject the user picks (e.g. a template) adds its own key.
 */
export type QuoteComparisonValue = {
  operator?: QuoteComparisonSubOperator
  /** Which part of the pricing to compare. Pricing rows only. */
  comparison?: QuotePricingComparison
}

export const readQuoteComparisonValue = (
  value: unknown
): QuoteComparisonValue => {
  if (typeof value === 'object' && value !== null) {
    return value as QuoteComparisonValue
  }
  return {}
}

/** A row with no stored comparison compares the pricing as a whole. */
export const readPricingComparison = (
  value: unknown
): QuotePricingComparison => {
  const { comparison } = readQuoteComparisonValue(value)
  return isQuotePricingComparison(comparison) ? comparison : 'ALL'
}

/**
 * Reads the outcome `compare_quote_pricing` published for one subject, so the
 * comparing itself lives in a deploy rather than in every saved workflow
 * version.
 */
export const pricingOutcomeExpression = ({
  semanticType,
  scope,
  subOperator
}: {
  semanticType: SemanticType
  scope: string
  subOperator: QuoteComparisonSubOperator
}): string => {
  const outcome = scopedSemanticTypeRuntimePath({ semanticType, scope })

  return match(subOperator)
    .with('IS_EQUAL', () => `${outcome} === true`)
    .with('IS_NOT_EQUAL', () => `${outcome} !== true`)
    .exhaustive()
}
